import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Container } from '@mui/material';
import { Refresh, Home } from '@mui/icons-material';
import { recordEvent } from '../services/metrics';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorId: string;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            hasError: false,
            error: null,
            errorId: '',
        };
    }

    public static getDerivedStateFromError(error: Error): State {
        // Generate a unique error ID for tracking
        const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        return {
            hasError: true,
            error,
            errorId,
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error for debugging (in production, send to monitoring service)
        console.error('Error Boundary caught an error:', {
            error: error.message,
            stack: error.stack,
            errorInfo,
            errorId: this.state.errorId,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
        });

        // Call custom error handler if provided
        this.props.onError?.(error, errorInfo);

        // Record error in metrics
        recordEvent('error_occurred', {
            error_message: error.message,
            error_type: 'react_error_boundary',
            error_id: this.state.errorId,
            component_stack: errorInfo.componentStack,
            url: window.location.href,
        });
    }

    private handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorId: '',
        });
    };

    private handleGoHome = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <Container maxWidth="sm" sx={{ mt: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Something went wrong
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                We encountered an unexpected error. This has been logged and our team will investigate.
                            </Typography>
                        </Alert>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                Error ID: {this.state.errorId}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<Refresh />}
                                onClick={this.handleRetry}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Home />}
                                onClick={this.handleGoHome}
                            >
                                Go Home
                            </Button>
                        </Box>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box sx={{ mt: 3, textAlign: 'left' }}>
                                <Alert severity="warning">
                                    <Typography variant="subtitle2">
                                        Development Error Details:
                                    </Typography>
                                    <Typography variant="body2" component="pre" sx={{
                                        fontSize: '0.75rem',
                                        mt: 1,
                                        overflow: 'auto',
                                        maxHeight: '200px'
                                    }}>
                                        {this.state.error.message}
                                        {'\n\n'}
                                        {this.state.error.stack}
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </Container>
            );
        }

        return this.props.children;
    }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
    Component: React.ComponentType<P>,
    fallback?: ReactNode,
    onError?: (error: Error, errorInfo: ErrorInfo) => void
) => {
    const WrappedComponent = (props: P) => (
        <ErrorBoundary fallback={fallback} onError={onError}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
    return WrappedComponent;
};

export default ErrorBoundary;
